import type { ReactNode } from 'react';
import type { StateCreator } from 'zustand';

type BooleanUpdater = boolean | ((previous: boolean) => boolean);
type ContentUpdater = ReactNode | ((previous: ReactNode) => ReactNode);

const resolveBooleanUpdater = (updater: BooleanUpdater, previous: boolean): boolean =>
  typeof updater === 'function' ? updater(previous) : updater;

const resolveContentUpdater = (updater: ContentUpdater, previous: ReactNode): ReactNode =>
  typeof updater === 'function' ? updater(previous) : updater;

/** Files list right-panel mode: detail / AI summary / AI document chat (selection sync differs). */
export type RightPanelKind = 'detail' | 'aiSummary' | 'aiDocumentChat';

/** Initial right-panel state; ReactNode is not persisted. */
const initialRightPanelState = {
  rightPanelContent: null as ReactNode,
  isRightPanelOpen: false,
  hasRightPanelCloseButton: true,
  isRightPanelCheckDirtyOnClose: true,
  rightPanelKind: 'detail' as RightPanelKind,
};

export interface RightPanelSlice {
  rightPanelContent: ReactNode;
  isRightPanelOpen: boolean;
  hasRightPanelCloseButton: boolean;
  isRightPanelCheckDirtyOnClose: boolean;
  rightPanelKind: RightPanelKind;
  setRightPanelContent: (content: ContentUpdater) => void;
  setHasRightPanelCloseButton: (value: BooleanUpdater) => void;
  setIsRightPanelCheckDirtyOnClose: (value: BooleanUpdater) => void;
  setRightPanelKind: (kind: RightPanelKind) => void;
  openRightPanel: (closeButtonVisible?: boolean) => void;
  closeRightPanel: () => void;
  toggleRightPanel: () => void;
  resetRightPanelState: () => void;
}

export const createRightPanelSlice: StateCreator<RightPanelSlice, [], [], RightPanelSlice> = (
  set,
) => ({
  ...initialRightPanelState,

  setRightPanelContent: (content) => {
    set((state) => ({
      rightPanelContent: resolveContentUpdater(content, state.rightPanelContent),
    }));
  },

  setHasRightPanelCloseButton: (value) => {
    set((state) => ({
      hasRightPanelCloseButton: resolveBooleanUpdater(value, state.hasRightPanelCloseButton),
    }));
  },

  setIsRightPanelCheckDirtyOnClose: (value) => {
    set((state) => ({
      isRightPanelCheckDirtyOnClose: resolveBooleanUpdater(
        value,
        state.isRightPanelCheckDirtyOnClose,
      ),
    }));
  },

  setRightPanelKind: (kind) => {
    set({ rightPanelKind: kind });
  },

  openRightPanel: (closeButtonVisible = true) => {
    set({
      isRightPanelOpen: true,
      hasRightPanelCloseButton: closeButtonVisible,
    });
  },

  closeRightPanel: () => {
    set({ isRightPanelOpen: false, rightPanelKind: 'detail' });
  },

  toggleRightPanel: () => {
    set((state) => ({
      isRightPanelOpen: !state.isRightPanelOpen,
      ...(!state.isRightPanelOpen ? {} : { rightPanelKind: 'detail' as RightPanelKind }),
    }));
  },

  resetRightPanelState: () => {
    set({ ...initialRightPanelState });
  },
});
